import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import {
	SystemMessageIconLabelKey,
	SystemMessageIconType,
	type SystemMessageIconType as SystemMessageIconTypeValue,
} from 'constants/system-messages.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';

export const SYSTEM_MESSAGE_ICON_PATHS: Record<SystemMessageIconTypeValue, string> = {
	[SystemMessageIconType.GENERAL]: '/icons/system-message-general.svg',
	[SystemMessageIconType.OPERATIONAL]: '/icons/system-message-operational.svg',
	[SystemMessageIconType.ALERT]: '/icons/system-message-alert.svg',
	[SystemMessageIconType.FEATURE]: '/icons/system-message-feature.svg',
	[SystemMessageIconType.HOLIDAY]: '/icons/system-message-holiday.svg',
	[SystemMessageIconType.POLICY]: '/icons/system-message-policy.svg',
};

const ICON_ORDER: SystemMessageIconTypeValue[] = [
	SystemMessageIconType.GENERAL,
	SystemMessageIconType.OPERATIONAL,
	SystemMessageIconType.ALERT,
	SystemMessageIconType.FEATURE,
	SystemMessageIconType.HOLIDAY,
	SystemMessageIconType.POLICY,
];

interface IconTilePickerProps {
	value: SystemMessageIconTypeValue;
	onChange: (value: SystemMessageIconTypeValue) => void;
	disabled?: boolean;
}

export const IconTilePicker = ({ value, onChange, disabled }: IconTilePickerProps) => {
	const { forestGreen, basicDeeoGreen, mediumGreen, inputFocusGreen, formTitle, muted } = useTheme().palette.root;

	const t = useAppSelector(selectTranslations);

	return (
		<Stack direction="row" flexWrap="wrap" gap="10px">
			{ICON_ORDER.map((iconType) => {
				const selected = value === iconType;
				return (
					<Box
						key={iconType}
						onClick={() => !disabled && onChange(iconType)}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '8px',
							padding: '14px 12px',
							borderRadius: '8px',
							background: selected ? alpha(forestGreen, 0.06) : '#fff',
							border: `2px solid ${selected ? forestGreen : mediumGreen}`,
							cursor: disabled ? 'not-allowed' : 'pointer',
							width: '96px',
							flex: '0 0 auto',
							transition: 'all 0.15s',
							'&:hover': disabled
								? undefined
								: { borderColor: inputFocusGreen, background: selected ? alpha(forestGreen, 0.06) : '#fafcfc' },
						}}
					>
						<Box
							sx={{
								width: 44,
								height: 44,
								borderRadius: '50%',
								background: alpha(selected ? forestGreen : basicDeeoGreen, selected ? 0.12 : 0.07),
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<img src={SYSTEM_MESSAGE_ICON_PATHS[iconType]} width={22} height={22} alt="" />
						</Box>
						<Typography
							sx={{
								// !important: theme variants carry responsiveFontSizes media queries that
								// otherwise override a plain sx fontSize (matches the create-form labels).
								fontSize: '11.5px !important',
								lineHeight: 1.35,
								color: selected ? formTitle : muted,
								fontWeight: selected ? 600 : 400,
								textAlign: 'center',
							}}
						>
							{t(SystemMessageIconLabelKey[iconType])}
						</Typography>
					</Box>
				);
			})}
		</Stack>
	);
};
